// IM-CTX-REQ-002/026: nova empresa contratante (= novo tenant). O usuário vira proprietário e a análise do site começa.
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { PendingInvitationDto } from '@intelimove/contracts';
import { organizationAdapter } from '../data/adapters';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/Toast';
import { PageHead } from '../components/Widgets';
import { companyAdapter } from '../data/context';
import { BrandLogo } from '../components/BrandLogo';
import { friendlyError } from '../lib/errors';
import { newId } from '../lib/id';

export function NewCompany() {
  const { me, replaceMe, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = !me?.organizations.length;
  // Mesma chave de idempotência enquanto os dados do formulário não mudam: duplo clique, reenvio após falha de rede
  // ou resposta perdida devolvem a MESMA empresa (o servidor deduplica pela chave), nunca uma segunda.
  const attempt = useRef<{ sig: string; key: string } | null>(null);
  // IM-XPR-006: colega convidado entra na organização existente em vez de criar outra (dados ficariam separados).
  const [invites, setInvites] = useState<PendingInvitationDto[]>([]);
  useEffect(() => {
    const ctl = new AbortController();
    organizationAdapter.myInvitations(ctl.signal).then((r) => setInvites(r.items)).catch(() => setInvites([]));
    return () => ctl.abort();
  }, []);
  const join = async (inv: PendingInvitationDto) => {
    setBusy(true);
    setError(null);
    try {
      const r = await organizationAdapter.accept({ invitationId: inv.id });
      replaceMe(r.me);
      toast(`Você entrou em ${inv.organizationName}.`);
      navigate('/', { replace: true });
    } catch (err) {
      setError(friendlyError(err, 'Não foi possível aceitar o convite agora. Tente novamente.'));
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      const website = String(f.get('website') ?? '').trim();
      const companyName = String(f.get('companyName') ?? '').trim();
      const sig = `${companyName}\n${website}`;
      if (attempt.current?.sig !== sig) attempt.current = { sig, key: `ui-${newId()}` };
      const r = await companyAdapter.create({ companyName, ...(website ? { website } : {}) }, attempt.current.key);
      replaceMe(r.me);
      toast(r.analysisRunId ? 'Empresa criada. Estamos analisando o site — as sugestões aparecem aqui para você revisar.' : 'Empresa criada. Complete o contexto da sua empresa.');
      navigate('/configuracoes/contexto/visao-geral', { replace: true, state: { justCreated: true, analyzing: Boolean(r.analysisRunId) } });
    } catch (err) {
      setError(friendlyError(err, 'Não foi possível criar a empresa agora. Tente novamente. Se o problema continuar, entre em contato com o suporte.'));
    } finally {
      setBusy(false);
    }
  };
  const invitations = invites.length ? (
    <section className="card source-form" aria-labelledby="invites-title">
      <h2 id="invites-title" className="card-title">Sua equipe já está no InteliMove</h2>
      <p className="card-sub">Aceite o convite para trabalhar na mesma base de empresas, pesquisas e decisões. Só crie uma empresa nova se for outra empresa contratante.</p>
      <ul className="invite-list">
        {invites.map((i) => (
          <li key={i.id}>
            <div><b>{i.organizationName}</b><div className="cell-sub wide">Convite{i.invitedBy ? ` de ${i.invitedBy}` : ''} · válido até {new Date(i.expiresAt).toLocaleDateString('pt-BR')}</div></div>
            <button type="button" className="cta" disabled={busy} onClick={() => join(i)}>Entrar em {i.organizationName}</button>
          </li>
        ))}
      </ul>
    </section>
  ) : null;
  const body = (
    <form className="card source-form" onSubmit={submit} aria-labelledby="new-company-title">
      <div className="card-head"><div><h2 id="new-company-title">{first ? 'Comece pela sua empresa' : 'Nova empresa contratante'}</h2>
        <p className="card-sub">Cada empresa contratante tem uma única organização, compartilhada por toda a equipe. Se a sua empresa já usa o InteliMove, peça um convite a um administrador. Para pesquisar concorrentes ou clientes, não crie uma empresa: cadastre-os dentro do contexto.</p></div></div>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="form-grid">
        <div className="field"><label htmlFor="companyName">Nome da empresa</label><input id="companyName" name="companyName" required minLength={2} maxLength={120} placeholder="Ex.: BugKillers" /></div>
        <div className="field"><label htmlFor="website">Site</label><input id="website" name="website" maxLength={2000} placeholder="empresa.com.br (opcional)" inputMode="url" autoComplete="url" /></div>
      </div>
      <p className="card-sub">Com o site, a InteliMove lê páginas públicas (sobre, produtos, serviços, segmentos, contato) e sugere informações para você revisar. Sem site, você preenche manualmente.</p>
      <div className="form-actions">
        {first ? <button type="button" className="secondary" onClick={logout}>Sair</button> : <button type="button" className="secondary" onClick={() => navigate(-1)}>Cancelar</button>}
        <button className="cta" disabled={busy}>{busy ? 'Criando…' : 'Criar empresa'}</button>
      </div>
    </form>
  );
  if (first) return <main className="onboarding"><BrandLogo variant="public" />{invitations}{body}</main>;
  return <section aria-labelledby="page-title"><PageHead eyebrow="Empresas" title="Nova empresa" subtitle="Cria uma nova conta de empresa, com você como proprietário." />{invitations}{body}</section>;
}
